import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import {
  LayoutDashboard, Building2, Cpu, Wrench, FileSpreadsheet,
  AlertTriangle, Settings, Menu, X, LogOut, Sun, Moon, Home,
  Upload, Radio, History, Banknote, Signal, ClipboardList, MapPin,
  Bell
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const navItems = [
  { href: '/gestion4sat/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/gestion4sat/ordenes', label: 'Órdenes de Trabajo', icon: ClipboardList },
  { href: '/gestion4sat/mapa', label: 'Mapa Operativo', icon: MapPin },
  { href: '/gestion4sat/empresas', label: 'Empresas', icon: Building2 },
  { href: '/gestion4sat/dispositivos', label: 'Dispositivos', icon: Cpu },
  { href: '/gestion4sat/servicios', label: 'Servicios', icon: Wrench },
  { href: '/gestion4sat/facturacion', label: 'Pre-facturación', icon: FileSpreadsheet },
  { href: '/gestion4sat/conciliacion', label: 'Conciliación', icon: Banknote },
  { href: '/gestion4sat/conectividad', label: 'Conectividad', icon: Signal },
  { href: '/gestion4sat/alertas', label: 'Alertas', icon: AlertTriangle, badge: true },
  { href: '/gestion4sat/importar', label: 'Importar Datos', icon: Upload },
  { href: '/gestion4sat/config', label: 'Configuración', icon: Settings },
];

export function G4sLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alertasPendientes, setAlertasPendientes] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/gestion4sat/alertas?estado=pendiente&limit=1`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (r.ok) {
          const d = await r.json();
          setAlertasPendientes(d.total || 0);
        }
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const sidebarContent = (mobile = false) => (
    <div className={`flex flex-col h-full ${mobile ? 'p-4' : 'p-6'} bg-card`}>
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
          <Radio className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-black uppercase tracking-tight text-base leading-tight">Gestión 4sat</div>
          <div className="text-xs text-muted-foreground -mt-0.5">GPS Operaciones</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1">
        {navItems.map(item => {
          const Icon = item.icon;
          const active = location.pathname.startsWith(item.href);
          const showBadge = item.badge && alertasPendientes > 0;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active
                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
              data-testid={`g4s-nav-${item.href.split('/').pop()}`}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{item.label}</span>
              {showBadge && <Badge className="bg-red-500 text-white text-xs h-5 px-1.5">{alertasPendientes}</Badge>}
            </Link>
          );
        })}
      </nav>

      <div className="space-y-2 pt-4 border-t">
        <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground" onClick={toggleTheme}>
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          {isDark ? 'Modo Claro' : 'Modo Oscuro'}
        </Button>
        <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-muted-foreground" onClick={() => navigate('/home')}>
          <Home className="h-4 w-4" /> Inicio
        </Button>
        <Button variant="ghost" size="sm" className="w-full justify-start gap-2 text-red-500 hover:text-red-600 hover:bg-red-500/10" onClick={logout}>
          <LogOut className="h-4 w-4" /> Cerrar Sesión
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block w-64 border-r border-border bg-card sticky top-0 h-screen overflow-y-auto">
        {sidebarContent(false)}
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="fixed left-0 top-0 bottom-0 w-72 bg-card shadow-xl">
            {sidebarContent(true)}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card/80 backdrop-blur-sm flex items-center px-4 gap-3 sticky top-0 z-30">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)} data-testid="g4s-menu-toggle">
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => navigate('/gestion4sat/alertas')}
            data-testid="g4s-bell-notifications"
          >
            <Bell className="h-5 w-5" />
            {alertasPendientes > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                {alertasPendientes > 99 ? '99+' : alertasPendientes}
              </span>
            )}
          </Button>
          <span className="text-xs text-muted-foreground hidden sm:block">
            {user?.nombre} ({user?.rol})
          </span>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
